import { useState, useEffect, useCallback, useRef } from 'react';
import { getDataWithQueryString } from '@componentAPIs';
import { RMAXLogger } from '@RMAXLogger';
import appConstants from '@appConstants';
import {
  transformRoutesData,
  flattenShipmentsFromRoutes,
  buildRouteProMappingQueryParams
} from '../utils';

interface UseRouteProMappingParams {
  planDate?: string;
  terminalCode?: string;
  scenarioId?: string;
  enabled?: boolean;
}

interface UseRouteProMappingResult {
  routes: any[];
  allShipments: any[];
  loading: boolean;
  error: string | null;
  gridStatus: string | null;
}

const useRouteProMapping = ({
  planDate,
  terminalCode,
  scenarioId,
  enabled = true
}: UseRouteProMappingParams): UseRouteProMappingResult => {
  const [routes, setRoutes] = useState<any[]>([]);
  const [allShipments, setAllShipments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gridStatus, setGridStatus] = useState<string | null>(null);
  const activeRequestRef = useRef(0);

  const fetchData = useCallback(async () => {
    if (!planDate || !terminalCode || !scenarioId) {
      activeRequestRef.current++;
      setRoutes([]);
      setAllShipments([]);
      setError(null);
      setGridStatus(null);
      setLoading(false);
      return;
    }

    const requestId = ++activeRequestRef.current;

    setLoading(true);
    setError(null);
    setGridStatus(appConstants.GRID_STATUS.LOADING);

    try {
      const queryParams = buildRouteProMappingQueryParams({
        planDate,
        terminalCode,
        scenarioId
      });

      const response = await getDataWithQueryString('GetRouteProMapping', queryParams);

      if (requestId !== activeRequestRef.current) return;

      if (response?.status === 200) {
        const routesData = response.data || [];
        const transformedRoutes = transformRoutesData(routesData);
        const shipments = flattenShipmentsFromRoutes(routesData);

        setRoutes(transformedRoutes);
        setAllShipments(shipments);
        setGridStatus(appConstants.GRID_STATUS.SUCCESS);
      } else {
        throw new Error('Failed to fetch route pro mapping data');
      }
    } catch (fetchError: any) {
      if (requestId !== activeRequestRef.current) return;

      const errorMessage = fetchError?.response?.data?.message || 'Failed to fetch data';
      setError(errorMessage);
      setGridStatus(appConstants.GRID_STATUS.ERROR);
      setRoutes([]);
      setAllShipments([]);

      RMAXLogger.error('Failed to fetch Route Pro Mapping', {
        planDate,
        terminalCode,
        scenarioId,
        error: errorMessage
      });
    } finally {
      if (requestId === activeRequestRef.current) {
        setLoading(false);
      }
    }
  }, [planDate, terminalCode, scenarioId]);

  useEffect(() => {
    if (enabled) {
      fetchData();
    } else {
      activeRequestRef.current++;
      setRoutes([]);
      setAllShipments([]);
      setError(null);
      setGridStatus(null);
      setLoading(false);
    }

    return () => {
      activeRequestRef.current++;
    };
  }, [fetchData, enabled]);

  return {
    routes,
    allShipments,
    loading,
    error,
    gridStatus
  };
};

export default useRouteProMapping;
